import { useState } from "react";
import type { NPSConfig, Question, Response } from "../../lib/types";
import { submitResponse } from "../../lib/api";

type Props = {
  question: Question;
  streamId?: string | null;
  onSubmitted: (row: Response) => void;
  onError: (message: string) => void;
};

const BAND_LABEL: Record<"detractor" | "passive" | "promoter", string> = {
  detractor: "Detraktor",
  passive: "Passiv",
  promoter: "Promoter",
};

function bandFor(rating: number): "detractor" | "passive" | "promoter" {
  if (rating <= 6) return "detractor";
  if (rating <= 8) return "passive";
  return "promoter";
}

export function NPSAnswer({ question, streamId, onSubmitted, onError }: Props) {
  const cfg = question.config as NPSConfig;
  const [rating, setRating] = useState<number | null>(null);
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const whyRequired = cfg.why_required && rating !== null;
  const canSubmit =
    rating !== null && !submitting && (!whyRequired || why.trim().length > 0);

  async function handleSubmit() {
    if (rating === null) return;
    setSubmitting(true);
    try {
      const row = await submitResponse({
        questionId: question.id,
        text: why.trim(),
        rating,
        streamId,
      });
      onSubmitted(row);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Single row of 11 buttons — must NEVER wrap on phones. Wrapping splits
          the scale visually (0–7 on top, 8–10 below) and biases respondents
          toward the top row. min-h instead of min-w keeps tap targets ≥ 44 px
          tall while letting widths shrink to share the row equally. */}
      <div className="flex w-full gap-0.5 sm:gap-1">
        {Array.from({ length: 11 }, (_, i) => i).map((n) => {
          const selected = rating === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className={[
                "flex-1 min-w-0 min-h-[44px] inline-flex items-center justify-center rounded-lg px-0 text-[15px] sm:text-base font-semibold tabular-nums transition border",
                selected
                  ? "bg-rc-berry text-rc-cream border-rc-berry shadow-berry"
                  : "bg-rc-paper text-rc-ink border-rc-ink/10 hover:bg-rc-cream hover:border-rc-ink/25",
              ].join(" ")}
              aria-pressed={selected}
              aria-label={`Bewertung ${n}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-sm text-rc-ink/70">
        <span>{cfg.labels.min}</span>
        <span>{cfg.labels.max}</span>
      </div>

      {rating !== null && (
        <div className="text-sm text-rc-ink/60" aria-live="polite">
          {BAND_LABEL[bandFor(rating)]}
        </div>
      )}

      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-rc-ink">
          {cfg.why_prompt}
          {whyRequired ? " *" : ""}
        </span>
        <textarea
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={3}
          className="rounded-lg border border-rc-ink/10 bg-rc-paper p-3 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
          placeholder="Optional — aber Ihre Begründung hilft uns sehr."
        />
      </label>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="rounded-xl bg-rc-berry px-6 py-3 text-rc-cream font-medium shadow-berry transition disabled:opacity-50 disabled:shadow-none"
      >
        {submitting ? "Wird gesendet…" : "Antwort senden"}
      </button>
    </div>
  );
}
